import type { Chord } from '@tunasong/music-lib'
import { ChordLib } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { useSearchParams } from '@tunasong/ui-lib'
import { useState, type FC } from 'react'
import { ChordProgressionPicker } from '../chord-progression-picker/chord-progression-picker.js'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { ScaleSelect } from '../scale/scale-select.js'
import { MelodyWriter } from './melody-writer.js'
import { parseMelodyWriterChordsParam, parseMelodyWriterScaleParam } from './melody-writer-page-params.js'
import styles from './melody-writer-page.module.css'

const MIN_CHORDS = 1
const MAX_CHORDS = 8

const noop = (): void => undefined

const serializeChordList = (chords: Chord[]): string =>
  chords.map(c => ChordLib.getName(c)).join(',')

const buildRelatedTools = (scale: Scale, chords: Chord[]): RelatedTool[] => {
  const scaleParam = `${scale.root}-${scale.type ?? 'major'}`
  const chordsParam = serializeChordList(chords)
  const tools: RelatedTool[] = [
    {
      targetRoute: `/app/cadences?scale=${encodeURIComponent(scaleParam)}&chords=${encodeURIComponent(chordsParam)}`,
      lexiconId: 'crosslink-melody-to-cadence',
    },
  ]
  // Reharm Lab v1 is major-only. Omit the link in non-major contexts to keep
  // the cross-link experience honest.
  if (scale.type === 'major') {
    tools.push({
      targetRoute: `/app/reharm?key=${encodeURIComponent(scale.root)}`,
      lexiconId: 'crosslink-melody-to-reharm',
    })
  }
  return tools
}

/**
 * Melody Writer standalone-route page-host wrapper.
 *
 * Reads `?scale=<root>-<type>` and `?chords=<comma-list>` from the URL,
 * derives defaults via `getDefaultProgressionForScale`, and renders the
 * existing `<MelodyWriter>` drawer with `open={true}` plus pickers for the
 * user to change scale + progression.
 *
 * Spec: docs/superpowers/specs/2026-05-20-drawer-tools-standalone-routes-design.md
 */
export const MelodyWriterPage: FC = () => {
  const [searchParams] = useSearchParams()
  const [scale, setScale] = useState<Scale>(() =>
    parseMelodyWriterScaleParam(searchParams.get('scale'))
  )
  const [chords, setChords] = useState<Chord[]>(() =>
    parseMelodyWriterChordsParam(searchParams.get('chords'), scale)
  )

  const handleScaleChange = (next: Scale | null): void => {
    if (!next) {
      return
    }
    setScale(next)
    if (next.type !== scale.type) {
      setChords(parseMelodyWriterChordsParam(null, next))
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Melody Writer</h1>
        <div className={styles.pickers}>
          <ScaleSelect value={scale} onChange={handleScaleChange} variant="all" allowNone={false} />
          <ChordProgressionPicker
            keyScale={scale}
            chords={chords}
            minChords={MIN_CHORDS}
            maxChords={MAX_CHORDS}
            onChange={setChords}
          />
        </div>
      </header>
      <MelodyWriter scale={scale} chords={chords} open={true} onClose={noop} />
      <RelatedTools tools={buildRelatedTools(scale, chords)} />
    </div>
  )
}
